// AddContractModal.tsx
import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Plus, Trash2, DollarSign, FileText, CheckSquare, Shield } from 'lucide-react';
import { useContractsStore } from '../../store/contractsStore';
import { usePartnershipsStore } from '../../store/partnershipsStore';
import { Contract, CreateContractData } from '../../services/contracts.service';

interface AddContractModalProps {
    isOpen: boolean;
    onClose: () => void;
    editingContract?: Contract | null;
    preselectedPartnership?: any;
}

const AddContractModal: React.FC<AddContractModalProps> = ({ isOpen, onClose, editingContract, preselectedPartnership }) => {
    const { createContract, updateContract } = useContractsStore();
    const { partnerships, fetchPartnerships } = usePartnershipsStore();

    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Form state
    const [formData, setFormData] = useState<{
        partnershipId: string;
        title: string;
        totalValue: number;
        startDate: string;
        endDate: string;
        paymentType: 'upfront' | 'milestone' | 'completion' | 'net30' | 'net60';
        milestones: { name: string; percentage: number; dueDate: string }[];
        deliverables: { contentType: string; title: string; description: string; deadline: string }[];
        ftcDisclosure: boolean;
        usageRightsYears: number;
        exclusivityDays: number;
        revisionsAllowed: number;
        killFeePercentage: number;
        autoRenewal: boolean;
        terminationNoticeDays: number;
        notes: string;
        tags: string[];
    }>({
        partnershipId: '',
        title: '',
        totalValue: 0,
        startDate: '',
        endDate: '',
        paymentType: 'milestone',
        milestones: [{ name: '', percentage: 0, dueDate: '' }],
        deliverables: [{ contentType: '', title: '', description: '', deadline: '' }],
        ftcDisclosure: true,
        usageRightsYears: 1,
        exclusivityDays: 0,
        revisionsAllowed: 3,
        killFeePercentage: 0,
        autoRenewal: false,
        terminationNoticeDays: 30,
        notes: '',
        tags: [],
    });

    const [tagInput, setTagInput] = useState('');

    // Fetch partnerships on mount
    useEffect(() => {
        fetchPartnerships();
    }, [fetchPartnerships]);

    // Reset form when modal opens/closes or when editing contract changes
    useEffect(() => {
        if (isOpen) {
            if (editingContract) {
                setFormData({
                    partnershipId: editingContract.partnershipId,
                    title: editingContract.title,
                    totalValue: editingContract.totalValue,
                    startDate: editingContract.startDate.split('T')[0],
                    endDate: editingContract.endDate.split('T')[0],
                    paymentType: editingContract.paymentTerms?.type as any || 'milestone',
                    milestones: editingContract.paymentTerms?.milestones?.map(m => ({
                        name: m.name,
                        percentage: m.percentage,
                        dueDate: m.dueDate?.split('T')[0] || '',
                    })) || [{ name: '', percentage: 0, dueDate: '' }],
                    deliverables: editingContract.deliverables?.map(d => ({
                        contentType: (d as any).contentType || '',
                        title: d.title,
                        description: d.description || '',
                        deadline: d.deadline?.split('T')[0] || '',
                    })) || [{ contentType: '', title: '', description: '', deadline: '' }],
                    ftcDisclosure: editingContract.compliance?.ftcDisclosure ?? true,
                    usageRightsYears: editingContract.compliance?.usageRightsYears ?? 1,
                    exclusivityDays: editingContract.compliance?.exclusivityDays ?? 0,
                    revisionsAllowed: editingContract.compliance?.revisionsAllowed ?? 3,
                    killFeePercentage: editingContract.compliance?.killFeePercentage ?? 0,
                    autoRenewal: editingContract.autoRenewal,
                    terminationNoticeDays: editingContract.terminationNoticeDays,
                    notes: editingContract.notes || '',
                    tags: editingContract.tags || [],
                });
            } else if (preselectedPartnership) {
                // Pre-fill from partnership
                const partnershipId = String(preselectedPartnership.id);
                const today = new Date();
                const startDate = today.toISOString().split('T')[0];

                let endDate = '';
                if (preselectedPartnership.deliveryDate) {
                    endDate = new Date(preselectedPartnership.deliveryDate).toISOString().split('T')[0];
                } else {
                    const threeMonthsLater = new Date(today);
                    threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
                    endDate = threeMonthsLater.toISOString().split('T')[0];
                }

                const deliverables = preselectedPartnership.deliverables && preselectedPartnership.deliverables.length > 0
                    ? preselectedPartnership.deliverables.map((d: { contentType?: string; title: string; description?: string; deadline?: string }) => ({
                        contentType: d.contentType || '',
                        title: d.title || '',
                        description: d.description || '',
                        deadline: d.deadline ? new Date(d.deadline).toISOString().split('T')[0] : endDate,
                    }))
                    : [{ contentType: '', title: '', description: '', deadline: '' }];

                setFormData({
                    partnershipId,
                    title: `${preselectedPartnership.brand} - ${preselectedPartnership.contentType || 'Partnership'} Agreement`,
                    totalValue: preselectedPartnership.value || 0,
                    startDate,
                    endDate,
                    paymentType: 'milestone',
                    milestones: [{ name: '', percentage: 0, dueDate: '' }],
                    deliverables,
                    ftcDisclosure: true,
                    usageRightsYears: 1,
                    exclusivityDays: 0,
                    revisionsAllowed: 3,
                    killFeePercentage: 0,
                    autoRenewal: false,
                    terminationNoticeDays: 30,
                    notes: preselectedPartnership.notes || '',
                    tags: preselectedPartnership.tags || [],
                });
            } else {
                setFormData({
                    partnershipId: '',
                    title: '',
                    totalValue: 0,
                    startDate: '',
                    endDate: '',
                    paymentType: 'milestone',
                    milestones: [{ name: '', percentage: 0, dueDate: '' }],
                    deliverables: [{ contentType: '', title: '', description: '', deadline: '' }],
                    ftcDisclosure: true,
                    usageRightsYears: 1,
                    exclusivityDays: 0,
                    revisionsAllowed: 3,
                    killFeePercentage: 0,
                    autoRenewal: false,
                    terminationNoticeDays: 30,
                    notes: '',
                    tags: [],
                });
            }
            setCurrentStep(1);
        }
    }, [isOpen, editingContract, preselectedPartnership]);

    const steps = [
        { number: 1, title: 'Basic Info', icon: FileText },
        { number: 2, title: 'Payment Terms', icon: DollarSign },
        { number: 3, title: 'Deliverables', icon: CheckSquare },
        { number: 4, title: 'Compliance', icon: Shield },
    ];

    const contentTypes = [
        'Video',
        'Photo Post',
        'Story',
        'Carousel',
        'Reel / Short Video',
        'Long Video',
        'Blog / Article',
        'UGC (Brand can reuse)'
    ];

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Handle partnership selection - auto-fill fields from partnership
    const handlePartnershipChange = (partnershipId: string) => {
        const selectedPartnership = partnerships.find(p => String(p.id) === partnershipId);

        if (selectedPartnership) {
            // Calculate dates - start today, end based on delivery date or 3 months from now
            const today = new Date();
            const startDate = today.toISOString().split('T')[0];

            let endDate = '';
            if (selectedPartnership.deliveryDate) {
                endDate = new Date(selectedPartnership.deliveryDate).toISOString().split('T')[0];
            } else {
                const threeMonthsLater = new Date(today);
                threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
                endDate = threeMonthsLater.toISOString().split('T')[0];
            }

            const deliverables = selectedPartnership.deliverables && selectedPartnership.deliverables.length > 0
                ? selectedPartnership.deliverables.map(d => ({
                    contentType: d.contentType || '',
                    title: d.title || '',
                    description: d.description || '',
                    deadline: d.deadline ? new Date(d.deadline).toISOString().split('T')[0] : endDate,
                }))
                : [{ contentType: '', title: '', description: '', deadline: '' }];

            setFormData(prev => ({
                ...prev,
                partnershipId,
                title: `${selectedPartnership.brand} - ${selectedPartnership.contentType || 'Partnership'} Agreement`,
                totalValue: selectedPartnership.value || 0,
                startDate,
                endDate,
                deliverables,
                tags: selectedPartnership.tags || [],
                notes: selectedPartnership.notes || '',
            }));
        } else {
            setFormData(prev => ({ ...prev, partnershipId }));
        }
    };

    // Milestone handlers
    const addMilestone = () => {
        setFormData(prev => ({
            ...prev,
            milestones: [...prev.milestones, { name: '', percentage: 0, dueDate: '' }],
        }));
    };

    const updateMilestone = (index: number, field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            milestones: prev.milestones.map((m, i) => i === index ? { ...m, [field]: value } : m),
        }));
    };

    const removeMilestone = (index: number) => {
        setFormData(prev => ({
            ...prev,
            milestones: prev.milestones.filter((_, i) => i !== index),
        }));
    };

    // Deliverable handlers
    const addDeliverable = () => {
        setFormData(prev => ({
            ...prev,
            deliverables: [...prev.deliverables, { contentType: '', title: '', description: '', deadline: '' }],
        }));
    };

    const updateDeliverable = (index: number, field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            deliverables: prev.deliverables.map((d, i) => i === index ? { ...d, [field]: value } : d),
        }));
    };

    const removeDeliverable = (index: number) => {
        setFormData(prev => ({
            ...prev,
            deliverables: prev.deliverables.filter((_, i) => i !== index),
        }));
    };

    // Tag handlers
    const addTag = () => {
        if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, tagInput.trim()],
            }));
            setTagInput('');
        }
    };

    const removeTag = (tag: string) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(t => t !== tag),
        }));
    };

    // Submit handler
    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const contractData: CreateContractData = {
                partnershipId: formData.partnershipId,
                title: formData.title,
                totalValue: formData.totalValue,
                startDate: formData.startDate,
                endDate: formData.endDate,
                paymentTerms: {
                    type: formData.paymentType,
                    milestones: formData.milestones
                        .filter(m => m.name)
                        .map(m => ({
                            name: m.name,
                            percentage: m.percentage,
                            status: 'pending' as const,
                            ...(m.dueDate ? { dueDate: m.dueDate } : {}),
                        })),
                },
                deliverables: formData.deliverables
                    .filter(d => d.title)
                    .map(d => ({
                        title: d.title,
                        status: 'pending' as const,
                        ...(d.description ? { description: d.description } : {}),
                        ...(d.deadline ? { deadline: d.deadline } : {}),
                    })),
                compliance: {
                    ftcDisclosure: formData.ftcDisclosure,
                    brandApproval: false,
                    usageRightsYears: formData.usageRightsYears,
                    exclusivityDays: formData.exclusivityDays,
                    revisionsAllowed: formData.revisionsAllowed,
                    killFeePercentage: formData.killFeePercentage,
                },
                autoRenewal: formData.autoRenewal,
                terminationNoticeDays: formData.terminationNoticeDays,
                ...(formData.notes ? { notes: formData.notes } : {}),
                ...(formData.tags.length > 0 ? { tags: formData.tags } : {}),
            };

            if (editingContract) {
                await updateContract(editingContract.id, contractData);
            } else {
                await createContract(contractData);
            }
            onClose();
        } catch (error) {
            console.error('Failed to save contract:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
                {/* Backdrop */}
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

                {/* Modal */}
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            {editingContract ? 'Edit Contract' : 'Create New Contract'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Step Indicator */}
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            {steps.map((step, index) => (
                                <React.Fragment key={step.number}>
                                    <button
                                        onClick={() => setCurrentStep(step.number)}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${currentStep === step.number
                                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                                            : currentStep > step.number
                                                ? 'text-green-600 dark:text-green-400'
                                                : 'text-gray-400 dark:text-gray-500'
                                            }`}
                                    >
                                        <step.icon className="w-4 h-4" />
                                        <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
                                    </button>
                                    {index < steps.length - 1 && (
                                        <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.number
                                            ? 'bg-green-500'
                                            : 'bg-gray-200 dark:bg-gray-700'
                                            }`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto max-h-[50vh]">
                        {/* Step 1: Basic Info */}
                        {currentStep === 1 && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Partnership <span className="text-red-500">*</span>
                                    </label>
                                    {editingContract ? (
                                        <div className="w-full px-4 py-2.5 bg-gray-100 dark:bg-gray-600 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300">
                                            {editingContract.partnership?.brand || partnerships.find(p => String(p.id) === String(formData.partnershipId))?.brand || 'Unknown Partnership'}
                                        </div>
                                    ) : (
                                        <select
                                            value={formData.partnershipId}
                                            onChange={(e) => handlePartnershipChange(e.target.value)}
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                            required
                                        >
                                            <option value="">Select a partnership...</option>
                                            {partnerships.map((p) => (
                                                <option key={p.id} value={p.id}>
                                                    {p.brand} - {p.contact}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Contract Title <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => handleInputChange('title', e.target.value)}
                                        placeholder="e.g., Q1 2024 Sponsorship Agreement"
                                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Total Value <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                        <input
                                            type="number"
                                            value={formData.totalValue}
                                            onChange={(e) => handleInputChange('totalValue', parseFloat(e.target.value) || 0)}
                                            placeholder="0.00"
                                            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Start Date <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.startDate}
                                            onChange={(e) => handleInputChange('startDate', e.target.value)}
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            End Date <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.endDate}
                                            onChange={(e) => handleInputChange('endDate', e.target.value)}
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Notes
                                    </label>
                                    <textarea
                                        value={formData.notes}
                                        onChange={(e) => handleInputChange('notes', e.target.value)}
                                        placeholder="Additional notes..."
                                        rows={3}
                                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Tags
                                    </label>
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {formData.tags.map((tag) => (
                                            <span
                                                key={tag}
                                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-lg text-sm"
                                            >
                                                {tag}
                                                <button onClick={() => removeTag(tag)} className="hover:text-primary-900 dark:hover:text-primary-200">
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={tagInput}
                                            onChange={(e) => setTagInput(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                                            placeholder="Add tag..."
                                            className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={addTag}
                                            className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                        >
                                            <Plus className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Payment Terms */}
                        {currentStep === 2 && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Payment Type
                                    </label>
                                    <select
                                        value={formData.paymentType}
                                        onChange={(e) => handleInputChange('paymentType', e.target.value)}
                                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                    >
                                        <option value="upfront">Upfront Payment</option>
                                        <option value="milestone">Milestone Based</option>
                                        <option value="completion">Upon Completion</option>
                                        <option value="net30">Net 30</option>
                                        <option value="net60">Net 60</option>
                                    </select>
                                </div>

                                {formData.paymentType === 'milestone' && (
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Payment Milestones
                                            </label>
                                            <button
                                                type="button"
                                                onClick={addMilestone}
                                                className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                                            >
                                                <Plus className="w-4 h-4" />
                                                Add Milestone
                                            </button>
                                        </div>
                                        <div className="space-y-3">
                                            {formData.milestones.map((milestone, index) => (
                                                <div key={index} className="flex gap-2 items-start">
                                                    <input
                                                        type="text"
                                                        value={milestone.name}
                                                        onChange={(e) => updateMilestone(index, 'name', e.target.value)}
                                                        placeholder="Milestone name"
                                                        className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 text-sm"
                                                    />
                                                    <input
                                                        type="number"
                                                        value={milestone.percentage}
                                                        onChange={(e) => updateMilestone(index, 'percentage', parseFloat(e.target.value) || 0)}
                                                        placeholder="%"
                                                        className="w-20 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 text-sm"
                                                    />
                                                    <input
                                                        type="date"
                                                        value={milestone.dueDate}
                                                        onChange={(e) => updateMilestone(index, 'dueDate', e.target.value)}
                                                        className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm"
                                                    />
                                                    {formData.milestones.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeMilestone(index)}
                                                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                            Total: {formData.milestones.reduce((sum, m) => sum + m.percentage, 0)}% (should equal 100%)
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 3: Deliverables */}
                        {currentStep === 3 && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Deliverables
                                    </label>
                                    <button
                                        type="button"
                                        onClick={addDeliverable}
                                        className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Deliverable
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    {formData.deliverables.map((deliverable, index) => (
                                        <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                                    Deliverable {index + 1}
                                                </span>
                                                {formData.deliverables.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeDeliverable(index)}
                                                        className="p-1 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="space-y-3">
                                                {/* Row 1: Content Type + Deadline */}
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Content Type</label>
                                                        <select
                                                            value={deliverable.contentType}
                                                            onChange={(e) => updateDeliverable(index, 'contentType', e.target.value)}
                                                            className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                                        >
                                                            <option value="">Select type...</option>
                                                            {contentTypes.map(type => (
                                                                <option key={type} value={type}>{type}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Deadline</label>
                                                        <input
                                                            type="date"
                                                            value={deliverable.deadline}
                                                            onChange={(e) => updateDeliverable(index, 'deadline', e.target.value)}
                                                            className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                                        />
                                                    </div>
                                                </div>
                                                {/* Row 2: Title */}
                                                <div>
                                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Title</label>
                                                    <input
                                                        type="text"
                                                        value={deliverable.title}
                                                        onChange={(e) => updateDeliverable(index, 'title', e.target.value)}
                                                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                                        placeholder="e.g., Product Review Video"
                                                    />
                                                </div>
                                                {/* Row 3: Description */}
                                                <div>
                                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Description (optional)</label>
                                                    <textarea
                                                        value={deliverable.description}
                                                        onChange={(e) => updateDeliverable(index, 'description', e.target.value)}
                                                        rows={2}
                                                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                                                        placeholder="Specific requirements for this deliverable..."
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Step 4: Compliance & Terms */}
                        {currentStep === 4 && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                    <div>
                                        <h4 className="font-medium text-gray-900 dark:text-white">FTC Disclosure Required</h4>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Content must include #ad or #sponsored</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.ftcDisclosure}
                                            onChange={(e) => handleInputChange('ftcDisclosure', e.target.checked)}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-500 peer-checked:bg-primary-600"></div>
                                    </label>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Usage Rights (Years)
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.usageRightsYears}
                                            onChange={(e) => handleInputChange('usageRightsYears', parseInt(e.target.value) || 0)}
                                            min="0"
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Exclusivity (Days)
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.exclusivityDays}
                                            onChange={(e) => handleInputChange('exclusivityDays', parseInt(e.target.value) || 0)}
                                            min="0"
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Revisions Allowed
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.revisionsAllowed}
                                            onChange={(e) => handleInputChange('revisionsAllowed', parseInt(e.target.value) || 0)}
                                            min="0"
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Kill Fee (%)
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.killFeePercentage}
                                            onChange={(e) => handleInputChange('killFeePercentage', parseInt(e.target.value) || 0)}
                                            min="0"
                                            max="100"
                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                    <div>
                                        <h4 className="font-medium text-gray-900 dark:text-white">Auto-Renewal</h4>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Automatically renew when contract ends</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.autoRenewal}
                                            onChange={(e) => handleInputChange('autoRenewal', e.target.checked)}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-500 peer-checked:bg-primary-600"></div>
                                    </label>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Termination Notice (Days)
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.terminationNoticeDays}
                                        onChange={(e) => handleInputChange('terminationNoticeDays', parseInt(e.target.value) || 30)}
                                        min="1"
                                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        <button
                            type="button"
                            onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : onClose()}
                            className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            {currentStep > 1 ? 'Back' : 'Cancel'}
                        </button>

                        {currentStep < 4 ? (
                            <button
                                type="button"
                                onClick={() => setCurrentStep(currentStep + 1)}
                                className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={isSubmitting || !formData.partnershipId || !formData.title || !formData.startDate || !formData.endDate}
                                className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Saving...' : editingContract ? 'Update Contract' : 'Create Contract'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddContractModal;
